import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import {
  DifficultyEngine,
  GameSession,
  type GameController,
  type Transport,
  type TransportOutbound,
} from '@rendever/games-sdk';

import { useHost } from '@/host/HostContext';
import { isEmbedded } from '@/host/isEmbedded';
import { hasGame, loadGame, type GameModule } from '@/games/registry';
import { createTransport } from '@/transport/createTransport';

import './GameHostPage.css';

type LoadState =
  | { status: 'loading' }
  | { status: 'not-found' }
  | { status: 'not-implemented' }
  | { status: 'ready'; module: GameModule }
  | { status: 'error'; message: string };

const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.1.0';

/**
 * The way off an error screen — standalone only.
 *
 * Embedded, this would be a link to the whole catalog offered at the moment a
 * resident is most likely to press anything on screen. The host's own exit is
 * already there and is the correct one, so the error states keep their words
 * and lose this button.
 */
function BackToGamesAction() {
  if (isEmbedded()) return null;
  return (
    <div style={{ marginTop: 'var(--space-6)' }}>
      <Link to="/" className="btn btn--secondary">
        Back to games
      </Link>
    </div>
  );
}

export function GameHostPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { mode, client, config } = useHost();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  // Base transport from the mode/client. Memoized so a new effect run does
  // not rebuild the wrapper below on every render.
  const baseTransport: Transport = useMemo(
    () => createTransport({ mode, client }),
    [mode, client],
  );

  // Wrap the transport so we can observe `session_ended` and route to results
  // without making games aware of routing.
  const transportRef = useRef<Transport>(baseTransport);

  // 1) Resolve the game module for this slug.
  useEffect(() => {
    let cancelled = false;
    if (!hasGame(slug)) {
      setState({ status: 'not-found' });
      return;
    }
    setState({ status: 'loading' });
    loadGame(slug)
      .then((mod) => {
        if (cancelled) return;
        if (!mod) {
          setState({ status: 'not-implemented' });
          return;
        }
        setState({ status: 'ready', module: mod });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Unknown error',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // 2) Once we have a module and the DOM container, mount it.
  useEffect(() => {
    if (state.status !== 'ready') return;
    const container = containerRef.current;
    if (!container) return;

    const abort = new AbortController();
    let controller: GameController | null = null;
    let disposed = false;

    // Wrap the transport to detect session_ended for navigation.
    const observingTransport: Transport = {
      async send(event: TransportOutbound): Promise<void> {
        await baseTransport.send(event);
        if (event.kind === 'session_ended') {
          if (disposed) return;
          /* Standalone: finishing a round returns to the games hub, which is
             where a player came from.

             Embedded: it must not. This is the one door out of the host's
             catalog that opens with no deliberate tap at all — a resident
             simply finishes a game of Mahjong and lands on the full thirteen-
             title wall, past whatever the host was gating. The host put this
             title on screen and the host's own exit is the way off it, so we
             stay put and let the round's end screen stand. */
          if (mode === 'iframe') return;
          navigate('/');
        }
      },
    };
    transportRef.current = observingTransport;

    const session = new GameSession({
      gameSlug: state.module.definition.slug,
      residentId: config.residentId ?? 'standalone-demo',
      surface: config.surface,
      inputMode: config.inputMode,
      deviceKind: surfaceToDeviceKind(config.surface),
      clientAppVersion: APP_VERSION,
      transport: observingTransport,
      difficulty: 1,
    });

    // Minimal multi-axis difficulty engine the SDK can adapt as the game runs.
    const difficulty = new DifficultyEngine({
      axes: [
        { name: 'pace', min: 0, max: 10, current: 1, tracking: 'performance' },
        { name: 'level', min: 0, max: 10, current: 0, tracking: 'level' },
      ],
      targetSuccessRate: 0.8,
      smoothing: 0.3,
    });

    state.module
      .mount({
        container,
        session,
        difficulty,
        signal: abort.signal,
      })
      .then((c) => {
        if (disposed) {
          c.dispose();
          return;
        }
        controller = c;
      })
      .catch((err: unknown) => {
        if (disposed) return;
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Mount failed',
        });
      });

    return () => {
      disposed = true;
      abort.abort();
      controller?.dispose();
    };
  }, [state, baseTransport, navigate, mode, config.residentId, config.surface, config.inputMode]);

  if (state.status === 'loading') {
    return (
      <div className="status-block" aria-live="polite">
        <div>
          <p className="status-block__title">Loading…</p>
          <p>Just a moment.</p>
        </div>
      </div>
    );
  }

  if (state.status === 'not-found') {
    return (
      <div className="status-block" role="alert">
        <div>
          <p className="status-block__title">We couldn’t find that game.</p>
          <p>It may have been renamed or removed.</p>
          <BackToGamesAction />
        </div>
      </div>
    );
  }

  if (state.status === 'not-implemented') {
    return (
      <div className="status-block" aria-live="polite">
        <div>
          <p className="status-block__title">Still being built.</p>
          <p>
            <strong>{slug}</strong> is on its way — check back soon.
          </p>
          <BackToGamesAction />
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="status-block" role="alert">
        <div>
          <p className="status-block__title">We hit a small snag.</p>
          <p>{state.message}</p>
          <BackToGamesAction />
        </div>
      </div>
    );
  }

  return (
    <div className="game-host">
      <div
        ref={containerRef}
        className="game-host__stage"
        aria-label={`${state.module.definition.title} game stage`}
      />
    </div>
  );
}

function surfaceToDeviceKind(
  surface: 'resident-app' | 'signage' | 'tablet' | 'standalone',
): 'phone' | 'tablet' | 'web' | 'tv' {
  switch (surface) {
    case 'signage':
      return 'tv';
    case 'tablet':
      return 'tablet';
    case 'resident-app':
      return 'tablet';
    case 'standalone':
      return 'web';
  }
}
